import { phoneNumberSchema, type AssetMedia } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { observer, use$, useObservable, useObserveEffect } from '@legendapp/state/react'
import { useEffect, useState } from 'react'
import { Pressable, View } from 'react-native'
import z from 'zod'
import { FileUpload } from '~/components/FileUpload'
import Icon from '~/components/Icon'
import { Body, Label } from '~/components/typography/Content.tsx'
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import {
  ContactMethodsManager,
  type ContactMethodsChangeEvent,
} from '~/components/user/ContactMethodsManager'
import { cn } from '~/lib/utils'

const emailSchema = z.email()

export interface RealtorProfileFormValues {
  name: string
  title: string
  email: string
  phoneNumber: string
  avatarUrl: string
}

export interface RealtorProfileFormState {
  formState: ReturnType<
    typeof useObservable<
      Record<keyof RealtorProfileFormValues, { value: string; isError: boolean }>
    >
  >
  isValid: boolean
  values: RealtorProfileFormValues
}

interface RealtorProfileFormProps {
  initialValues?: Partial<RealtorProfileFormValues>
  onValuesChange?: (values: RealtorProfileFormValues, isValid: boolean) => void
  onContactMethodsChange?: (values: ContactMethodsChangeEvent) => void
  showCompletionStatus?: boolean
  showAvatarUpload?: boolean
  useDirectContactInputs?: boolean
  className?: string
}

const RealtorProfileForm = observer(function RealtorProfileForm({
  initialValues = {},
  onValuesChange,
  onContactMethodsChange,
  showCompletionStatus = false,
  showAvatarUpload = true,
  useDirectContactInputs = false,
  className,
}: RealtorProfileFormProps) {
  const { t } = useTranslations('user')
  const formState = useObservable<
    Record<keyof RealtorProfileFormValues, { value: string; isError: boolean }>
  >({
    name: {
      value: initialValues.name ?? '',
      isError: !initialValues.name,
    },
    title: {
      value: initialValues.title ?? '',
      isError: false,
    },
    email: {
      value: initialValues.email ?? '',
      isError: initialValues.email ? !emailSchema.safeParse(initialValues.email).success : false,
    },
    phoneNumber: {
      value: initialValues.phoneNumber ?? '',
      isError: initialValues.phoneNumber
        ? !phoneNumberSchema.safeParse(initialValues.phoneNumber).success
        : false,
    },
    avatarUrl: {
      value: initialValues.avatarUrl ?? '',
      isError: false,
    },
  })

  useEffect(() => {
    if (initialValues.name !== undefined) {
      formState.name.assign({
        value: initialValues.name ?? '',
        isError: false,
      })
    }

    if (initialValues.title !== undefined) {
      formState.title.assign({
        value: initialValues.title ?? '',
        isError: false,
      })
    }

    if (initialValues.email !== undefined) {
      formState.email.assign({
        value: initialValues.email ?? '',
        isError: initialValues.email ? !emailSchema.safeParse(initialValues.email).success : false,
      })
    }

    if (initialValues.phoneNumber !== undefined) {
      formState.phoneNumber.assign({
        value: initialValues.phoneNumber ?? '',
        isError: !initialValues.phoneNumber,
      })
    }

    if (initialValues.avatarUrl !== undefined) {
      formState.avatarUrl.assign({
        value: initialValues.avatarUrl ?? '',
        isError: false,
      })
    }
  }, [initialValues, formState])

  const name = use$(() => formState.name.value.get() || '')
  const email = use$(() => formState.email.value.get() || '')
  const phoneNumber = use$(() => formState.phoneNumber.value.get() || '')
  const title = use$(() => formState.title.value.get() || '')
  const avatarUrl = use$(() => formState.avatarUrl.value.get() || '')

  const isValid = use$(() => {
    const state = formState.get()
    const hasName = !!state.name.value && !state.name.isError
    const hasEmail = !state.email.value || !state.email.isError
    const hasAvatar = !!state.avatarUrl.value

    const hasContact = !!(state.email.value || state.phoneNumber.value)
    const contactValid =
      (!!state.email.value && !state.email.isError) ||
      (!!state.phoneNumber.value && !state.phoneNumber.isError)
    const phoneValid = !!state.phoneNumber.value && !state.phoneNumber.isError

    return hasName && hasEmail && phoneValid && hasAvatar && hasContact && contactValid
  })

  useObserveEffect(() => {
    const state = formState.get()
    const currentValues = {
      name: state.name.value,
      title: state.title.value,
      email: state.email.value,
      phoneNumber: state.phoneNumber.value,
      avatarUrl: state.avatarUrl.value,
    }

    const hasName = !!state.name.value && !state.name.isError
    const hasEmail = !state.email.value || !state.email.isError
    const hasAvatar = !!state.avatarUrl.value

    const hasContact =
      (!!state.email.value && !state.email.isError) ||
      (!!state.phoneNumber.value && !state.phoneNumber.isError)

    const phoneValid = !!state.phoneNumber.value && !state.phoneNumber.isError

    const currentIsValid = hasName && hasEmail && phoneValid && hasAvatar && hasContact

    onValuesChange?.(currentValues, currentIsValid)
  })

  const [isEditingAvatar, setIsEditingAvatar] = useState(!avatarUrl && showAvatarUpload)

  const handleAvatarUpload = (media: AssetMedia) => {
    if (media.url) {
      formState.avatarUrl.assign({ value: media.url, isError: false })
      setIsEditingAvatar(false)
    }
  }

  return (
    <View className={cn('flex-1 gap-4 px-6', className)}>
      <Body className="text-pretty px-6 text-center text-neutral-700">
        {t(($) => $.profile.realtor.publicInfo)}
      </Body>

      {showAvatarUpload && (
        <>
          <View className="items-center gap-4">
            <View className="relative">
              <Avatar
                className={cn(
                  'size-32 border-2 border-card',
                  !avatarUrl && 'border-destructive/70'
                )}
                // eslint-disable-next-line i18next/no-literal-string
                alt="User avatar">
                <AvatarImage source={{ uri: avatarUrl }} />
                <AvatarFallback>
                  <Icon name="person-outline" size={24} className="text-destructive" />
                </AvatarFallback>
              </Avatar>
              <Pressable
                onPress={() => setIsEditingAvatar(!isEditingAvatar)}
                className={cn(
                  'absolute bottom-0 right-0 rounded-full border-2 border-background bg-primary p-2 active:bg-primary/80',
                  !avatarUrl && 'border-destructive/70'
                )}>
                <Icon name="pencil" size={16} className="text-primary-foreground" />
              </Pressable>
            </View>
            {!avatarUrl && (
              <View className="mb-4 flex-col text-center text-sm text-secondary">
                <Body className="text-center text-destructive">
                  {t(($) => $.profile.realtor.avatar.addClearPhoto)}
                </Body>
                <Body className="text-center text-neutral-700">
                  {t(($) => $.profile.realtor.avatar.tip)}
                </Body>
              </View>
            )}
          </View>

          {!!isEditingAvatar && (
            <View className="rounded-2xl bg-background/40 p-4">
              <Text className="mb-4 text-center font-semibold text-lg">
                {!avatarUrl
                  ? t(($) => $.profile.realtor.avatar.addTitle)
                  : t(($) => $.profile.realtor.avatar.changeTitle)}
              </Text>
              <FileUpload
                bucket="avatars"
                onUpload={handleAvatarUpload}
                maxSizeBytes={10 * 1024 * 1024}
                variant="transparent"
                className="w-full items-center justify-center"
              />
            </View>
          )}
        </>
      )}

      {!!showCompletionStatus && !isValid && (
        <View className="gap-3">
          <View
            className={cn(
              'mx-auto w-auto max-w-md flex-row items-center gap-3 rounded-xl bg-neutral-400/70 px-6 py-3 shadow-sm shadow-tertiary-foreground'
            )}>
            <Icon name="alert-circle-outline" size={16} className="text-neutral-800" />
            <Text className="text-neutral-800">
              {t(($) => $.profile.realtor.completionStatus.fillAllInfo)}
            </Text>
          </View>

          <Body className="mx-auto max-w-md text-pretty text-center text-sm/normal text-secondary">
            {t(($) => $.profile.realtor.completionStatus.cannotSendRequests)}
          </Body>
        </View>
      )}

      {!!showCompletionStatus && isValid && (
        <View
          className={cn(
            'mx-auto w-auto max-w-md flex-row items-center gap-3 rounded-xl bg-secondary px-6 py-3 shadow-sm shadow-tertiary-foreground'
          )}>
          <Icon name="checkmark-circle-outline" size={16} className="text-background" />
          <Text className="text-background">
            {t(($) => $.profile.realtor.completionStatus.profileReliable)}
          </Text>
        </View>
      )}

      <View className="w-full max-w-2xl gap-8">
        <View className="gap-2">
          <Label>{t(($) => $.profile.realtor.fields.name)}</Label>
          <Input
            value={name}
            onChangeText={(text) => {
              const nextValue = text.trimStart()

              formState.name.assign({
                value: nextValue,
                isError: nextValue.length === 0,
              })
            }}
            placeholder={t(($) => $.profile.realtor.fields.namePlaceholder)}
            className={cn(formState.name.isError.get() && 'border-destructive/70')}
            autoFocus={false}
            variant="box"
          />
        </View>

        <View className="gap-2">
          <Label>{t(($) => $.profile.realtor.fields.title)}</Label>
          <Body className="text-sm text-secondary">
            {t(($) => $.profile.realtor.fields.titleDescription)}
          </Body>
          <Input
            value={title}
            onChangeText={(text) => {
              const nextValue = text.trimStart()

              formState.title.assign({
                value: nextValue,
                isError: false,
              })
            }}
            placeholder={t(($) => $.profile.realtor.fields.titlePlaceholder)}
            autoFocus={false}
            variant="box"
          />
        </View>

        {useDirectContactInputs ? (
          <>
            <View className="gap-2">
              <Label>{t(($) => $.profile.realtor.fields.email)}</Label>
              <Input
                value={email}
                onChangeText={(text) => {
                  const nextValue = text.trim()
                  const isValidEmail = nextValue ? emailSchema.safeParse(nextValue).success : true
                  formState.email.assign({
                    value: nextValue,
                    isError: nextValue ? !isValidEmail : false,
                  })
                }}
                placeholder={t(($) => $.profile.realtor.fields.emailPlaceholder)}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                className={cn(formState.email.isError.get() && 'border-destructive/70')}
                variant="box"
              />
            </View>

            <View className="gap-2">
              <Label>{t(($) => $.profile.realtor.fields.phone)}</Label>
              <Input
                type="phone"
                value={phoneNumber}
                onChangeText={(_display, _countryCode, storedNumber, isValidPhone) => {
                  formState.phoneNumber.assign({
                    value: storedNumber,
                    isError: storedNumber ? !isValidPhone : true,
                  })
                }}
                placeholder={t(($) => $.profile.realtor.fields.phonePlaceholder)}
                variant="box"
                className={cn(formState.phoneNumber.isError.get() && 'border-destructive/70')}
              />
            </View>
          </>
        ) : (
          <ContactMethodsManager onChange={onContactMethodsChange} showEmail={false} showPhone />
        )}
      </View>
    </View>
  )
})

export default RealtorProfileForm
